import React from "react";
import { useApp } from "@/context/AppContext";
import { ADDR } from "./legalEn";
import { TERMS_EN, TERMS_DE } from "./terms";
import { PRIVACY_DE } from "./privacyDe";
import { PRIVACY_EN } from "./privacyEn";

const Page = ({ title, subtitle, children, testid }) => (
  <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 legal" data-testid={testid}>
    <div className="eyebrow text-primary">Soul Radar</div>
    <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter border-b border-foreground pb-3">{title}</h1>
    {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
    <div className="mt-6 space-y-6 text-[15px] leading-relaxed">{children}</div>
  </article>
);
const H = ({ children }) => <h2 className="font-display uppercase text-xl tracking-tight mt-8 mb-2">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => <ul className="list-disc pl-5 space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;

export function Imprint() {
  const { lang } = useApp();
  const de = lang !== "en";
  const addr = <p className="whitespace-pre-line">{de ? "Tim-Niklas Kubach\nLugbank 6\n96049 Bamberg\nDeutschland" : ADDR}</p>;
  return (
    <Page title={de ? "Impressum" : "Imprint"} subtitle={de ? "Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz)" : "Information pursuant to § 5 DDG (German Digital Services Act)"} testid="page-imprint">
      <P><strong>Soul Radar – Your Soul Event Finder</strong></P>
      {addr}
      <H>{de ? "Kontakt" : "Contact"}</H>
      <P>{de ? "E-Mail" : "E-mail"}: <a href="mailto:info@soulradar.net" className="underline" data-testid="imprint-email">info@soulradar.net</a><br />{de ? "Telefon" : "Phone"}: <a href="tel:+4995118090640" className="underline" data-testid="imprint-phone">+49 951 18090640</a></P>
      <H>{de ? "Verantwortlich für journalistisch-redaktionelle Inhalte gemäß § 18 Abs. 2 MStV" : "Responsible for journalistic and editorial content pursuant to § 18 (2) MStV"}</H>
      {addr}
      <H>{de ? "Hinweis zu den aufgeführten Veranstaltungen" : "Information about listed events"}</H>
      {de ? (<>
        <P>Soul Radar ist eine Plattform zur Information über Veranstaltungen. Soweit nicht ausdrücklich anders angegeben, ist Soul Radar weder Veranstalter noch Ticketanbieter der auf der Plattform aufgeführten Veranstaltungen.</P>
        <P>Die auf Soul Radar veröffentlichten Veranstaltungsinformationen können von Promotern, Veranstaltern oder registrierten Nutzern bereitgestellt werden. Eingereichte Veranstaltungen können vor der Veröffentlichung geprüft werden. Soul Radar kann jedoch nicht garantieren, dass alle Veranstaltungsinformationen richtig, vollständig oder aktuell sind.</P>
        <P>Termine, Uhrzeiten, Veranstaltungsorte, Line-ups, Eintrittspreise und andere Veranstaltungsdetails können sich kurzfristig ändern. Nutzer sollten wichtige Veranstaltungsinformationen daher vor einer Anreise, dem Kauf von Tickets oder anderen Vorkehrungen beim jeweiligen Veranstalter oder über die offiziellen Veranstaltungslinks überprüfen.</P>
        <P>Die gesetzliche Haftung bleibt unberührt.</P>
      </>) : (<>
        <P>Soul Radar is an event information platform. Unless expressly stated otherwise, Soul Radar is neither the organiser nor the ticket seller of the events listed on the platform.</P>
        <P>Event information published on Soul Radar may be provided by promoters, organisers or registered users. Submitted events may be reviewed before publication. However, Soul Radar cannot guarantee that all event information is accurate, complete or up to date.</P>
        <P>Dates, times, venues, line-ups, admission prices and other event details may change at short notice. Users should therefore verify important event information with the respective organiser or via the official event links before travelling, purchasing tickets or making other arrangements.</P>
        <P>Statutory liability remains unaffected.</P>
      </>)}
    </Page>
  );
}

const LINK_RX = /(https?:\/\/[^\s)]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
const linkify = (text) => text.split(LINK_RX).map((part, i) => {
  if (!part) return null;
  if (/^https?:\/\//.test(part)) return <a key={i} className="underline break-all" href={part} target="_blank" rel="noreferrer noopener">{part}</a>;
  if (/@/.test(part) && LINK_RX.test(part)) return <a key={i} className="underline" href={`mailto:${part}`}>{part}</a>;
  return part;
});

function LegalDoc({ doc, testid }) {
  return (
    <Page title={doc.title} subtitle={doc.subtitle} testid={testid}>
      {doc.blocks.map((b, i) => b.h ? <H key={i}>{b.h}</H> : b.ul ? <UL key={i} items={b.ul} /> : <P key={i}>{linkify(b.p)}</P>)}
    </Page>
  );
}

export function Privacy() {
  const { lang } = useApp();
  return <LegalDoc doc={lang === "en" ? PRIVACY_EN : PRIVACY_DE} testid="page-privacy" />;
}

export function Terms() {
  const { lang } = useApp();
  return <LegalDoc doc={lang === "en" ? TERMS_EN : TERMS_DE} testid="page-terms" />;
}

export function Guidelines() {
  return (
    <Page title="Event Submission Guidelines" testid="page-guidelines">
      <P>Soul Radar is made for genuine Soul events and related music culture. Please submit only events that fit the musical scope of Soul Radar.</P>
      <P>Before submitting, please make sure that:</P>
      <UL items={["event name, date, time and venue are correct;", "the selected genres reflect the actual music played;", "ticket and website links are current;", "DJs and artists are entered correctly;", "you have permission to use the uploaded flyer or image;", "the event has not already been submitted."]} />
      <P>Events are reviewed before publication. Soul Radar may request changes, reject submissions or remove incorrect, duplicate or unsuitable events.</P>
      <P className="font-display uppercase">Keep the faith. Keep the information accurate.</P>
    </Page>
  );
}
